import { Box, VStack } from '@chakra-ui/react'
import { isSameDay } from 'date-fns'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import type { DateRangePreset, DateRangePresetValue } from './datePicker.presets'

interface DatePickerPresetsProps {
  presets: DateRangePreset[]
  currentRange?: [Date | null, Date | null]
  onPresetSelect: (preset: DateRangePreset) => void
  selectedPreset?: DateRangePresetValue
}

export const DatePickerPresets = memo<DatePickerPresetsProps>(({ presets, currentRange, onPresetSelect, selectedPreset }) => {
  const { t } = useTranslation()

  const isPresetActive = (preset: DateRangePreset): boolean => {
    if (selectedPreset) {
      return selectedPreset === preset.value
    }
    if (!currentRange?.[0] || !currentRange?.[1]) return false
    const [presetStart, presetEnd] = preset.getDateRange()
    return isSameDay(currentRange[0], presetStart) && isSameDay(currentRange[1], presetEnd)
  }

  return (
    <Box backgroundColor='var(--color-muted)' h='100%' borderRight='1px solid' borderColor='gray.200'>
      <VStack spacing={0} maxH='280px' overflowY='auto' py={1} pr={3} pl={2}>
        {presets.map(preset => {
          const isActive = isPresetActive(preset)
          return (
            <Box
              key={preset.value}
              w='180px'
              px={2.5}
              py={1.5}
              cursor='pointer'
              bg={isActive ? 'blue.800' : 'transparent'}
              color={isActive ? 'white' : 'gray.700'}
              borderRadius='md'
              fontSize='13px'
              fontWeight={isActive ? 600 : 400}
              whiteSpace='nowrap'
              _hover={{
                bg: isActive ? 'blue.900' : 'blue.600',
                color: 'white',
              }}
              transition='all 0.2s'
              onClick={() => onPresetSelect(preset)}
              mb={0.5}
            >
              {t(`reports.modules.time_range_presets.${preset.value}`)}
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
})
